import { tokens } from "@blurb/codex-react";
import { UnstyledButton, createStyles } from "@mantine/core";
import { Flex, IconV3 } from "@rpi/openapi-core";
import React from "react";
import { Body } from "../../../components/Typography";
import { FieldFrame } from "../account-settings/FieldFrame";

export type TaxFileRowState =
  | { kind: "uploading"; progress: number; fileSize: number }
  | { kind: "uploaded" }
  | { kind: "failed" }
  | { kind: "rejected"; reason: string };

interface TaxFileRowProps {
  fileName: string;
  state: TaxFileRowState;
  onRemove: () => void;
  onRetry: () => void;
}

const useStyles = createStyles({
  iconButton: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 0,
    flexShrink: 0,
  },
  fileName: {
    flex: 1,
    minWidth: 0,
    overflowWrap: "anywhere",
  },
  reason: {
    color: tokens.color.semantic.text.danger,
  },
  success: {
    color: tokens.color.semantic.text.success,
  },
  byteCount: {
    color: tokens.color.semantic.text.inverseSubtle,
    whiteSpace: "nowrap",
  },
  percent: {
    color: tokens.color.semantic.text.bold,
    whiteSpace: "nowrap",
  },
  track: {
    flex: 1,
    height: 4,
    borderRadius: tokens.radius.full,
    backgroundColor: tokens.color.semantic.border.faint,
    overflow: "hidden",
  },
  fill: {
    height: "100%",
    backgroundColor: tokens.color.semantic.icon.accentBlue,
    transition: "width 150ms ease",
  },
});

function clampFraction(progress: number): number {
  return Math.min(Math.max(progress, 0), 1);
}

function toPercent(progress: number): number {
  return Math.round(clampFraction(progress) * 100);
}

function formatPercent(progress: number): string {
  return new Intl.NumberFormat(undefined, { style: "percent" }).format(clampFraction(progress));
}

function formatCount(value: number): string {
  return new Intl.NumberFormat().format(value);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${formatCount(bytes)}B`;
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) return `${formatCount(Math.round(kilobytes))}KB`;
  return `${formatCount(Number((kilobytes / 1024).toFixed(1)))}MB`;
}

export const TaxFileRow: React.FC<TaxFileRowProps> = ({ fileName, state, onRemove, onRetry }) => {
  const { classes } = useStyles();

  const renderTrailingControl = () => {
    if (state.kind === "failed") {
      return (
        <UnstyledButton type="button" className={classes.iconButton} aria-label={`Retry ${fileName}`} onClick={onRetry}>
          <IconV3 icon="refresh" size={16} color={tokens.color.semantic.icon.primary} />
        </UnstyledButton>
      );
    }
    if (state.kind === "uploading") {
      return (
        <UnstyledButton
          type="button"
          className={classes.iconButton}
          aria-label={`Remove ${fileName}`}
          onClick={onRemove}
        >
          <IconV3 icon="close" size={16} />
        </UnstyledButton>
      );
    }
    return (
      <UnstyledButton type="button" className={classes.iconButton} aria-label={`Delete ${fileName}`} onClick={onRemove}>
        <IconV3 icon="delete" size={16} />
      </UnstyledButton>
    );
  };

  return (
    <FieldFrame
      gap={tokens.spacing[2]}
      padding={`${tokens.spacing[2]} ${tokens.spacing[3]}`}
      data-testid="tax-file-row"
    >
      <Flex.Row justify="space-between" align="center" gap={tokens.spacing[2]} fullWidth>
        <Body size="base" weight="semibold" className={classes.fileName}>
          {fileName}
        </Body>
        {renderTrailingControl()}
      </Flex.Row>

      {state.kind === "uploading" && (
        <Flex.Row align="center" gap={tokens.spacing[2]} fullWidth>
          <Body size="sm" className={classes.byteCount}>
            {formatBytes(state.progress * state.fileSize)} of {formatBytes(state.fileSize)}
          </Body>
          <div
            className={classes.track}
            data-testid="tax-file-progress"
            role="progressbar"
            aria-label={`Uploading ${fileName}`}
            aria-valuenow={toPercent(state.progress)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className={classes.fill} style={{ width: `${toPercent(state.progress)}%` }} />
          </div>
          <Body size="sm" className={classes.percent}>
            {formatPercent(state.progress)}
          </Body>
        </Flex.Row>
      )}

      {state.kind === "uploaded" && (
        <Flex.Row align="center" gap={tokens.spacing[1]} data-testid="tax-file-success">
          <IconV3 icon="check_circle" size={16} color={tokens.color.semantic.icon.success} />
          <Body size="sm" className={classes.success}>
            Upload Complete
          </Body>
        </Flex.Row>
      )}

      {state.kind === "failed" && (
        <Body size="sm" className={classes.reason}>
          Upload failed. Please try again.
        </Body>
      )}

      {state.kind === "rejected" && (
        <Body size="sm" className={classes.reason}>
          {state.reason}
        </Body>
      )}
    </FieldFrame>
  );
};
